import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Download, Upload, FileSpreadsheet, AlertTriangle, Check, RefreshCw,
  Link2, Unlink, Plus, Pencil, Eye,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Iter 147 — Import/Export XLSX de Artículos Maestros y vinculaciones.
 * Flujo: Plantilla / Exportar → Editar → Subir → Preview → Decisiones → Aplicar.
 * Reutiliza la misma lógica backend (`link-master`/`unlink-master`) que el UI manual.
 */
export default function MasterPartsBulkPanel() {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [fileName, setFileName] = useState("");
  // Decisiones para artículos con stock > 0 al vincular: { row: true(transferir) | false(descartar) }
  const [stockDecisions, setStockDecisions] = useState({});
  // Stock inicial al desvincular: { row: number }
  const [unlinkInitial, setUnlinkInitial] = useState({});
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try {
      const r = await api.get("/master-parts/bulk/history");
      setHistory(r.data || []);
    } catch { /* no-op */ }
  };
  useEffect(() => { loadHistory(); }, []);

  const download = async (path, name) => {
    setBusy(true);
    try {
      const r = await api.get(path, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = name;
      a.click();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setFileName(file.name);
    setPreview(null);
    setStockDecisions({});
    setUnlinkInitial({});
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/master-parts/bulk/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
      toast.success("Vista previa cargada. Revisa antes de aplicar.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const canApply = () => {
    if (!preview) return false;
    if (preview.summary.errors > 0) return false;
    // Todas las decisiones de stock necesarias tomadas
    const needsDecision = (preview.link_new || []).filter((l) => l.needs_stock_decision);
    return needsDecision.every((l) => stockDecisions[l.row] === true || stockDecisions[l.row] === false);
  };

  const apply = async () => {
    if (!canApply()) {
      toast.error("Resuelve los errores y decisiones de stock antes de aplicar.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        new_masters: preview.new_masters || [],
        update_masters: preview.update_masters || [],
        new_parts: preview.new_parts || [],
        update_parts: preview.update_parts || [],
        new_categories: preview.new_categories || [],
        link_new: preview.link_new || [],
        link_change: preview.link_change || [],
        unlink: preview.unlink || [],
        stock_decisions: stockDecisions,
        unlink_initial_stock: unlinkInitial,
      };
      const r = await api.post("/master-parts/bulk/apply", payload);
      toast.success(`Aplicado: ${r.data.ok} OK · ${r.data.errors} errores`);
      // Descargar XLSX resultado automáticamente
      if (r.data.result_xlsx_b64) {
        const bin = atob(r.data.result_xlsx_b64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        const blob = new Blob([bytes], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `resultado_maestros_${new Date().toISOString().slice(0, 10)}.xlsx`;
        a.click();
      }
      setPreview(null);
      setFileName("");
      loadHistory();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const s = preview?.summary || {};

  return (
    <div className="space-y-4">
      <div>
        <div className="font-display font-bold text-lg flex items-center gap-2">
          <FileSpreadsheet size={18} className="text-[#6E8B3D]" /> Import / Export Artículos Maestros
        </div>
        <p className="text-sm text-[#525252] mt-1">
          Crea artículos maestros, vincula/desvincula artículos y modifica relaciones masivamente
          desde un XLSX. La lógica backend es la misma que la edición manual (sin duplicación de stock).
        </p>
      </div>

      {/* Botones de descarga + carga */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => download("/master-parts/bulk/template", "plantilla_maestros.xlsx")}
          disabled={busy}
          className="ds-btn"
          data-testid="mp-bulk-template"
        >
          <Download size={14} /> Descargar plantilla
        </button>
        <button
          onClick={() => download("/master-parts/bulk/export", `maestros_${new Date().toISOString().slice(0,10)}.xlsx`)}
          disabled={busy}
          className="ds-btn"
          data-testid="mp-bulk-export"
        >
          <Download size={14} /> Exportar inventario + maestros
        </button>
        <label className="ds-btn primary cursor-pointer" data-testid="mp-bulk-upload-label">
          <Upload size={14} /> Subir XLSX modificado
          <input
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={(e) => uploadFile(e.target.files?.[0])}
            disabled={busy}
            data-testid="mp-bulk-upload-input"
          />
        </label>
      </div>

      {fileName && !preview && (
        <div className="text-xs text-[#525252]">Archivo: <span className="font-mono">{fileName}</span></div>
      )}

      {/* Preview */}
      {preview && (
        <div className="rounded-2xl border border-[#E5E5E0] bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-[#E5E5E0] bg-gradient-to-b from-[#FDFDFB] to-[#FAFAF6] flex items-center justify-between">
            <div className="font-display font-bold flex items-center gap-2">
              <Eye size={16} /> Vista previa
            </div>
            <div className="text-xs text-[#525252]">
              {fileName ? <span className="font-mono">{fileName}</span> : null}
            </div>
          </div>

          {/* Resumen */}
          <div className="p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-9 gap-2">
            <SummaryChip label="Total filas" value={s.rows_total || 0} />
            <SummaryChip label="Cat. nuevas" value={s.new_categories || 0} tone="green" icon={<Plus size={12} />} />
            <SummaryChip label="Art. nuevos" value={s.new_parts || 0} tone="green" icon={<Plus size={12} />} />
            <SummaryChip label="Art. editados" value={s.update_parts || 0} icon={<Pencil size={12} />} />
            <SummaryChip label="Maestros nuevos" value={s.new_masters || 0} tone="green" icon={<Plus size={12} />} />
            <SummaryChip label="Maestros editados" value={s.update_masters || 0} icon={<Pencil size={12} />} />
            <SummaryChip label="Vínculos nuevos" value={s.link_new || 0} tone="green" icon={<Link2 size={12} />} />
            <SummaryChip label="Vínc. cambiados" value={s.link_change || 0} icon={<RefreshCw size={12} />} />
            <SummaryChip label="Errores" value={s.errors || 0} tone={s.errors ? "red" : "gray"} icon={<AlertTriangle size={12} />} />
          </div>

          {/* Errores */}
          {preview.errors?.length > 0 && (
            <Section title={`Errores (${preview.errors.length})`} tone="red">
              <SimpleTable rows={preview.errors} cols={[
                { key: "row", label: "Fila" },
                { key: "sku", label: "SKU" },
                { key: "message", label: "Motivo" },
              ]} />
            </Section>
          )}

          {/* Avisos */}
          {preview.warnings?.length > 0 && (
            <Section title={`Avisos (${preview.warnings.length})`}>
              <SimpleTable rows={preview.warnings} cols={[
                { key: "row", label: "Fila" },
                { key: "sku", label: "SKU" },
                { key: "message", label: "Aviso" },
              ]} />
            </Section>
          )}

          {/* Nuevas categorías */}
          {preview.new_categories?.length > 0 && (
            <Section title={`Categorías a crear (${preview.new_categories.length})`} tone="green">
              <SimpleTable rows={preview.new_categories} cols={[
                { key: "label", label: "Categoría" },
              ]} />
            </Section>
          )}

          {/* Artículos nuevos */}
          {preview.new_parts?.length > 0 && (
            <Section title={`Artículos nuevos (${preview.new_parts.length})`} tone="green">
              <SimpleTable rows={preview.new_parts} cols={[
                { key: "row", label: "Fila" },
                { key: "sku", label: "SKU" },
                { key: "name", label: "Nombre" },
                { key: "category", label: "Categoría" },
                { key: "sale_price", label: "Precio" },
                { key: "stock", label: "Stock" },
                { key: "master_sku", label: "→ Maestro" },
              ]} />
            </Section>
          )}

          {/* Artículos a actualizar */}
          {preview.update_parts?.length > 0 && (
            <Section title={`Artículos a actualizar (${preview.update_parts.length})`}>
              <SimpleTable rows={preview.update_parts.map((p) => ({
                ...p,
                changes: Object.entries(p.changes || {}).map(([k, v]) => `${k}=${v}`).join(", "),
              }))} cols={[
                { key: "row", label: "Fila" },
                { key: "sku", label: "SKU" },
                { key: "name", label: "Nombre" },
                { key: "changes", label: "Cambios" },
              ]} />
            </Section>
          )}

          {/* Maestros nuevos */}
          {preview.new_masters?.length > 0 && (
            <Section title={`Maestros nuevos (${preview.new_masters.length})`} tone="green">
              <SimpleTable rows={preview.new_masters} cols={[
                { key: "row", label: "Fila" },
                { key: "sku", label: "SKU" },
                { key: "name", label: "Nombre" },
                { key: "stock", label: "Stock" },
                { key: "min_stock", label: "Mín." },
                { key: "warranty_months", label: "Garantía" },
              ]} />
            </Section>
          )}

          {/* Maestros a editar */}
          {preview.update_masters?.length > 0 && (
            <Section title={`Maestros a editar (${preview.update_masters.length})`}>
              <SimpleTable rows={preview.update_masters.map((m) => ({
                ...m,
                changes: Object.entries(m.changes || {}).map(([k, v]) => `${k}=${v}`).join(", "),
              }))} cols={[
                { key: "row", label: "Fila" },
                { key: "sku", label: "SKU" },
                { key: "name", label: "Nombre" },
                { key: "changes", label: "Cambios" },
              ]} />
            </Section>
          )}

          {/* Vínculos nuevos con decisiones de stock */}
          {preview.link_new?.length > 0 && (
            <Section title={`Vínculos nuevos (${preview.link_new.length})`} tone="green">
              <div className="text-xs text-[#525252] px-4 pt-2">
                Los artículos con stock &gt; 0 requieren una decisión explícita antes de aplicar.
              </div>
              <div className="p-4 space-y-2">
                {preview.link_new.map((lk) => (
                  <div key={lk.row} className="rounded-lg border border-[#E5E5E0] px-3 py-2 flex flex-wrap items-center gap-3 text-sm">
                    <span className="font-mono text-[10px] text-[#525252]">#{lk.row}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{lk.name}</div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                        {lk.sku} → {lk.master_sku || "(nuevo)"}
                      </div>
                    </div>
                    {lk.needs_stock_decision ? (
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-amber-700 font-semibold">
                          {lk.current_stock} uds. propias
                        </span>
                        <button
                          type="button"
                          onClick={() => setStockDecisions((d) => ({ ...d, [lk.row]: true }))}
                          className={`ds-btn ${stockDecisions[lk.row] === true ? "primary" : ""}`}
                          data-testid={`mp-bulk-transfer-${lk.row}`}
                        >
                          Transferir al maestro
                        </button>
                        <button
                          type="button"
                          onClick={() => setStockDecisions((d) => ({ ...d, [lk.row]: false }))}
                          className={`ds-btn ${stockDecisions[lk.row] === false ? "primary" : ""}`}
                          data-testid={`mp-bulk-discard-${lk.row}`}
                        >
                          Descartar
                        </button>
                      </div>
                    ) : (
                      <span className="text-[10px] font-mono text-[#525252]">sin stock propio</span>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Cambios de maestro */}
          {preview.link_change?.length > 0 && (
            <Section title={`Cambios de maestro (${preview.link_change.length})`}>
              <SimpleTable rows={preview.link_change} cols={[
                { key: "row", label: "Fila" },
                { key: "sku", label: "SKU artículo" },
                { key: "name", label: "Nombre" },
                { key: "master_sku", label: "Nuevo maestro" },
              ]} />
            </Section>
          )}

          {/* Desvincular */}
          {preview.unlink?.length > 0 && (
            <Section title={`Desvincular (${preview.unlink.length})`}>
              <div className="text-xs text-[#525252] px-4 pt-2">
                Indica el stock inicial que tendrá cada artículo tras desvincular (nunca se copia del maestro).
              </div>
              <div className="p-4 space-y-2">
                {preview.unlink.map((lk) => (
                  <div key={lk.row} className="rounded-lg border border-[#E5E5E0] px-3 py-2 flex flex-wrap items-center gap-3 text-sm">
                    <span className="font-mono text-[10px] text-[#525252]">#{lk.row}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{lk.name}</div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">{lk.sku}</div>
                    </div>
                    <label className="text-xs text-[#525252] flex items-center gap-2">
                      Stock inicial:
                      <input
                        type="number"
                        min="0"
                        value={unlinkInitial[lk.row] ?? 0}
                        onChange={(e) => setUnlinkInitial((d) => ({ ...d, [lk.row]: Math.max(0, parseInt(e.target.value, 10) || 0) }))}
                        className="w-20 rounded-lg border border-[#E5E5E0] px-2 py-1 text-sm"
                        data-testid={`mp-bulk-unlink-initial-${lk.row}`}
                      />
                    </label>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Barra de acciones */}
          <div className="border-t border-[#E5E5E0] px-4 py-3 flex items-center justify-end gap-2 bg-[#FDFDFB]">
            <button onClick={() => { setPreview(null); setFileName(""); }} disabled={busy} className="ds-btn" data-testid="mp-bulk-cancel">
              Cancelar
            </button>
            <button
              onClick={apply}
              disabled={busy || !canApply()}
              className="ds-btn primary"
              data-testid="mp-bulk-apply"
            >
              <Check size={14} /> Confirmar importación
            </button>
          </div>
        </div>
      )}

      {/* Historial */}
      {history.length > 0 && !preview && (
        <div className="rounded-2xl border border-[#E5E5E0] bg-white p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-2">
            Historial ({history.length})
          </div>
          <div className="space-y-1">
            {history.slice(0, 5).map((h) => (
              <div key={h.id} className="flex items-center gap-3 text-xs text-[#525252] font-mono">
                <span>{new Date(h.at).toLocaleString("es-ES")}</span>
                <span className="flex-1">{h.user_name || "—"}</span>
                <span className="text-[#3F5222]">{h.ok} OK</span>
                {h.errors > 0 && <span className="text-red-700">{h.errors} err</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryChip({ label, value, tone = "gray", icon }) {
  const toneCls = {
    green: "bg-[#F0F4E8] text-[#3F5222] border-[#6E8B3D]/30",
    red:   "bg-red-50 text-red-700 border-red-200",
    gray:  "bg-[#F7F7F4] text-[#525252] border-[#E5E5E0]",
  }[tone];
  return (
    <div className={`rounded-lg border ${toneCls} px-2.5 py-1.5`}>
      <div className="font-mono text-[9px] uppercase tracking-widest flex items-center gap-1">
        {icon}{label}
      </div>
      <div className="font-display font-black text-xl">{value}</div>
    </div>
  );
}

function Section({ title, tone = "gray", children }) {
  const toneCls = {
    green: "border-t-[#6E8B3D]",
    red:   "border-t-red-500",
    gray:  "border-t-[#E5E5E0]",
  }[tone];
  return (
    <div className={`border-t-2 ${toneCls}`}>
      <div className="px-4 pt-3 font-mono text-[10px] uppercase tracking-widest text-[#525252]">{title}</div>
      {children}
    </div>
  );
}

function SimpleTable({ rows, cols }) {
  return (
    <div className="p-4 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-[#525252] border-b border-[#E5E5E0]">
            {cols.map((c) => (<th key={c.key} className="px-2 py-1.5">{c.label}</th>))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-[#E5E5E0] last:border-0">
              {cols.map((c) => (
                <td key={c.key} className="px-2 py-1.5 align-top">
                  {c.key === "sku" ? <span className="font-mono text-xs">{r[c.key]}</span> : (r[c.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
